import { useEffect } from 'react'

// Default Glue Monster branding icon for all agents
const DEFAULT_AGENT_ICON = '/assets/images/glue-monster-logo.svg'
const DEFAULT_AGENT_ICON_PNG = '/assets/images/glue-monster-logo.png' // For PWA manifest (requires PNG)

// Original BotNexus branding (to restore on unmount)
const BOTNEXUS_TITLE = 'BotNexus | Unified AI Agent Operations'
const BOTNEXUS_FAVICON = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='lucide lucide-bot h-5 w-5'%3E%3Cdefs%3E%3ClinearGradient id='orangeVioletGradient' x1='0%25' y1='0%25' x2='100%25' y2='0%25'%3E%3Cstop offset='0%25' stop-color='%23FFA500'/%3E%3Cstop offset='100%25' stop-color='%238A2BE2'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M12 8V4H8' stroke='url(%23orangeVioletGradient)'%3E%3C/path%3E%3Crect width='16' height='12' x='4' y='8' rx='2' stroke='url(%23orangeVioletGradient)'%3E%3C/rect%3E%3Crect x='1' y='12' width='3' height='3' fill='%23FFA500'%3E%3C/rect%3E%3Crect x='20' y='12' width='3' height='3' fill='%238A2BE2'%3E%3C/rect%3E%3Cline x1='15' y1='13' x2='15' y2='15' stroke='currentColor'%3E%3C/line%3E%3Cline x1='9' y1='13' x2='9' y2='15' stroke='currentColor'%3E%3C/line%3E%3C/svg%3E"

interface UseAgentPWAProps {
    agentName: string
    agentIcon?: string // Optional override for agent-specific icon
}

export function useAgentPWA({ agentName, agentIcon }: UseAgentPWAProps) {
    useEffect(() => {
        if (!agentName) return

        const iconSvg = agentIcon || DEFAULT_AGENT_ICON
        const iconPng = DEFAULT_AGENT_ICON_PNG // PWA manifest requires PNG

        // 1. Update Document Title
        document.title = agentName

        // 2. Update Favicon (browser tab icon)
        const faviconLink = document.getElementById('app-favicon') as HTMLLinkElement
        if (faviconLink) {
            faviconLink.href = iconSvg
        }

        // 3. Update Apple Touch Icon (supports SVG)
        const iconLink = document.getElementById('app-icon') as HTMLLinkElement
        if (iconLink) {
            iconLink.href = iconSvg
        }

        // 4. Generate Dynamic Manifest
        // Note: PWA manifest icons MUST be PNG/WebP (not SVG)
        // The agent name will be unique per installed app
        const manifestLink = document.getElementById('app-manifest') as HTMLLinkElement
        if (manifestLink) {
            // Use absolute URLs for icons (required for Android PWA install)
            const origin = window.location.origin
            const absoluteIconPng = `${origin}${iconPng}`

            // Blob-backed manifests cannot reliably resolve relative start URLs.
            // Use an absolute same-origin URL and omit query parameters so each
            // installed assistant opens on its canonical route.
            const startUrl = `${origin}${window.location.pathname}`

            const manifest = {
                name: agentName,
                short_name: agentName.length > 12 ? agentName.substring(0, 12) : agentName,
                start_url: startUrl,
                display: 'standalone',
                background_color: '#020617', // slate-950
                theme_color: '#020617',
                icons: [
                    {
                        src: absoluteIconPng,
                        sizes: '192x192',
                        type: 'image/png',
                        purpose: 'any maskable'
                    },
                    {
                        src: absoluteIconPng,
                        sizes: '512x512',
                        type: 'image/png',
                        purpose: 'any maskable'
                    }
                ]
            }

            const stringManifest = JSON.stringify(manifest)
            const blob = new Blob([stringManifest], { type: 'application/json' })
            const manifestURL = URL.createObjectURL(blob)

            manifestLink.href = manifestURL
        }

        // Cleanup: restore BotNexus branding when leaving agent page
        return () => {
            document.title = BOTNEXUS_TITLE

            const faviconLink = document.getElementById('app-favicon') as HTMLLinkElement
            if (faviconLink) {
                faviconLink.href = BOTNEXUS_FAVICON
            }

            // Revoke the manifest blob URL
            if (manifestLink) {
                URL.revokeObjectURL(manifestLink.href)
                manifestLink.removeAttribute('href')
            }
        }
    }, [agentName, agentIcon])
}
